import { useState } from 'react';
import { cn } from '@/utils/cn';

type CloudType = 'cobalt' | 'customer' | 'endCustomer';

interface CloudTabDisplayProps {
  tag?: string;
  title: string;
  description?: string;
  className?: string;
}

export const CloudTabDisplay = ({
  tag = 'DEPLOYMENT',
  title,
  description,
  className,
}: CloudTabDisplayProps) => {
  const [activeTab, setActiveTab] = useState<CloudType>('cobalt');

  const cloudImages = {
    cobalt: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Cobalt+cloud.svg',
    customer: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Customer+Cloud.svg',
    endCustomer: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/End+Customer+Cloud.png'
  };

  return (
    <section className={cn('py-24 px-4 md:px-6 lg:px-8 max-w-7xl mx-auto', className)}>
      <div className="text-center mb-16">
        {tag && (
          <div className="text-sm font-semibold text-blue-600 mb-3 tracking-widest">
            {tag}
          </div>
        )}
        <h2 className="text-3xl md:text-4xl font-bold mb-4">{title}</h2>
        {description && (
          <p className="text-gray-600 max-w-3xl mx-auto">{description}</p>
        )}
      </div>

      <div className="bg-gray-950 rounded-xl overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {/* Tabs section - now with dark background */}
          <div className="md:w-1/3">
            <div className="flex flex-row md:flex-col border-b md:border-b-0 md:border-r border-gray-800">
              <button
                className={cn(
                  'text-left pl-4 md:pl-8 pr-2 md:pr-4 py-3 md:py-8 text-xs md:text-base font-normal md:font-medium transition-all flex-1 md:flex-auto flex items-center justify-between whitespace-nowrap',
                  activeTab === 'cobalt'
                    ? 'bg-gray-900 text-blue-500'
                    : 'text-white hover:bg-gray-900 hover:text-blue-500'
                )}
                onClick={() => setActiveTab('cobalt')}
              >
                <span>Cobalt Cloud</span>
                <span className="hidden md:inline ml-2">→</span>
              </button>

              <button
                className={cn(
                  'text-left pl-4 md:pl-8 pr-2 md:pr-4 py-3 md:py-8 text-xs md:text-base font-normal md:font-medium transition-all flex-1 md:flex-auto flex items-center justify-between whitespace-nowrap',
                  activeTab === 'customer'
                    ? 'bg-gray-900 text-blue-500'
                    : 'text-white hover:bg-gray-900 hover:text-blue-500'
                )}
                onClick={() => setActiveTab('customer')}
              >
                <span>Customer Cloud</span>
                <span className="hidden md:inline ml-2">→</span>
              </button>

              <button
                className={cn(
                  'text-left pl-4 md:pl-8 pr-2 md:pr-4 py-3 md:py-8 text-xs md:text-base font-normal md:font-medium transition-all flex-1 md:flex-auto flex items-center justify-between whitespace-nowrap',
                  activeTab === 'endCustomer'
                    ? 'bg-gray-900 text-blue-500'
                    : 'text-white hover:bg-gray-900 hover:text-blue-500'
                )}
                onClick={() => setActiveTab('endCustomer')}
              >
                <span>End-Customer Cloud</span>
                <span className="hidden md:inline ml-2">→</span>
              </button>
            </div>
          </div>

          {/* Content section */}
          <div className="md:w-2/3 p-6 md:p-12 flex items-center justify-center">
            <img
              src={cloudImages[activeTab]}
              alt={`${activeTab} cloud diagram`}
              className="max-w-full h-auto object-contain"
            />
          </div>
        </div>
      </div>
    </section>
  );
};